import axios from "axios";
import { api } from "./client";
import { budgetFor, BUDGET_LOGIN_MS } from "./timeouts";
import type { Configuration } from "./configuration";
import {
  emitCloudNetworkError,
  getEdgeBaseUrl,
  isEdgeLocalMode,
} from "../lib/edge/edgeState";
import { getEdgeTicketForToken } from "../lib/edge/edgeTicket";
import { getRestaurantName } from "../lib/restaurant";

// Runtime config from /config.js (if available)
const runtimeBase =
  typeof window !== "undefined" ? window.__ENV?.VITE_API_BASE_URL : undefined;

// 1) runtime (/config.js), 2) build-time (Vite), 3) fallback localhost
const CLOUD_AUTH_BASE_URL =
  runtimeBase ?? import.meta.env.VITE_API_BASE_URL ?? "http://127.0.0.1:8000";

// Login-ите са със суров axios (заобикалят 401 интерсептора), затова сами
// избират адреса според режима (docs/edge.md): в локален режим и ПИН входът
// (`POST /user/login-with-code`), и входът с потребител+парола
// (`POST /user/login`) отиват към главната каса и връщат ЛОКАЛЕН билет като
// `access_token` (касата ги проверява по `code_hash`/`password_hash` от
// snapshot-а). Те са и ЕДИНСТВЕНИТЕ две заявки, които минават през касата в
// ограничен локален режим (§14.3.1) — затова не бива да минават през
// интерсептора на `api/client.ts`, който там отказва всичко останало.
const authBaseUrl = (): string =>
  isEdgeLocalMode()
    ? (getEdgeBaseUrl() ?? CLOUD_AUTH_BASE_URL)
    : CLOUD_AUTH_BASE_URL;

/**
 * Провален вход към облака е същата подсказка „не се стига до облака", каквато
 * всяка друга заявка дава през интерсептора (§15.4): терминал, от когото някой
 * е излязъл, не праща нищо друго — стои на екрана за вход — и без този сигнал
 * сондите никога не тръгват. Само подсказка: решава пак `edgeMode.ts`
 * (контролна сонда към облака + жива каса).
 *
 * Подсказка дава провал БЕЗ отговор и провал с 5xx (§16.4 №6, „половин
 * интернет": обратният proxy отговаря вместо мъртвия облак — иначе точно
 * екранът за вход никога не би стигнал до касата). Отказ по същество (грешна
 * парола, заключен акаунт, 429 „Твърде много опити") не значи нищо за мрежата.
 */
function hintCloudNetworkError(error: unknown) {
  if (isEdgeLocalMode()) return;
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status;
  if (status !== undefined && status < 500) return;
  emitCloudNetworkError();
}

// Request/Response Types
export interface LoginRequest {
  restaurant_name: string;
  username: string;
  password: string;
  force_logout_user_id?: number;
}

export interface ActiveUser {
  user_id: number;
  username: string;
  logged_in_at: string;
  last_activity_at: string;
}

export interface FullCapacityError {
  message: string;
  active_users: ActiveUser[];
}

export interface LoginWithCodeRequest {
  restaurant_name: string;
  code: string;
  shared_account_token?: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user_id: number;
  username: string;
  names: string | null;
  personal: boolean;
  restaurant_id: number;
  hasActiveSession?: boolean;
}

export interface RoleOut {
  id: number;
  name: string;
  description: string | null;
}

export interface PermissionOut {
  id: number;
  role_id: number;
  name: string;
  description: string | null;
}

export interface UserInfoResponse {
  id: number;
  username: string;
  names: string | null;
  personal: boolean;
  active: boolean;
  organization_access: boolean;
  code: string | null;
  created_at: string;
  roles: RoleOut[];
  permissions: PermissionOut[];
  restaurant_accesses: boolean;
  access: boolean; // Дали потребителят има потвърден достъп (от user_sessions)
  restaurant_display_name: string | null;
  // Режим „със склад" (organizations.uses_warehouse) — справката „Асортименти"
  // става „Рецепти". Optional за съвместимост със стар BE без полето.
  uses_warehouse?: boolean;
  // ВСИЧКИ настройки (на ресторанта + личните) — единственото място, откъдето
  // FE-то ги чете; `hooks/useConfiguration.ts`.
  configuration: Configuration;
}

export interface UserListItemOut {
  id: number;
  username: string;
  names: string | null;
  personal: boolean;
  // Шпикер екран — смислено само за акаунти с роля monitors; иначе false.
  shpicker: boolean;
  roles: RoleOut[];
  permissions: PermissionOut[];
  unreported_bills_amount?: number;
}

// API Functions
// Note: login and loginWithCode use direct axios to avoid auto-logout on 401
export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await axios
    .post<LoginResponse>(`${authBaseUrl()}/user/login`, credentials, {
      // Суровият axios заобикаля интерсептора, тоест и бюджета по маршрут —
      // затова редът от таблицата се слага тук на ръка. Без него екранът за
      // вход виси до таймаута на ОС-а (35–90 s) при спрян интернет, а точно
      // там човекът няма какво друго да гледа.
      timeout: budgetFor("post", "/user/login"),
    })
    .catch((error: unknown) => {
      hintCloudNetworkError(error);
      throw error;
    });

  // Токенът се записва ЕДИНСТВЕНО от login.fulfilled (beginTerminalSession) —
  // трите ключа (токен, вид акаунт, име) тръгват от едно място, а служителят
  // започва на чисто. Второ записване оттук раждаше междинно състояние:
  // нов токен + стар служител/флагове.

  return response.data;
}

export async function loginWithCode(
  credentials: LoginWithCodeRequest,
): Promise<LoginResponse> {
  // Към касата ПИН входът носи билета на терминала (X), ако го има —
  // касата минтва локален билет за служителя по code_hash от snapshot-а.
  // Без билет (ограничен режим, §14.3.1) заявката пак минава — това е един
  // от двата login-а, които касата приема без `X-Edge-Ticket`.
  const terminalTicket = isEdgeLocalMode()
    ? getEdgeTicketForToken(credentials.shared_account_token)
    : null;
  const response = await axios
    .post<LoginResponse>(
      `${authBaseUrl()}/user/login-with-code`,
      credentials,
      {
        // Виж `login` — същият заобиколен интерсептор, същият ред от таблицата.
        timeout: budgetFor("post", "/user/login-with-code"),
        ...(terminalTicket
          ? { headers: { "X-Edge-Ticket": terminalTicket } }
          : {}),
      },
    )
    .catch((error: unknown) => {
      hintCloudNetworkError(error);
      throw error;
    });

  return response.data;
}

/**
 * РАЗМЯНА на локалния билет за истински облачен токен —
 * `POST {cloud}/user/exchange-edge-ticket`.
 *
 * Билетът, който касата минтва при вход без интернет, НЕ е боклук за облака:
 * той е подписан с HMAC-SHA256 със СЕКРЕТА НА РЕСТОРАНТА, а облакът има и
 * секрета, и проверката (`app/core/edge_ticket.py`). Затова при връщане на
 * интернета той може да се РАЗМЕНИ за истински токен, вместо да се хвърли и
 * човекът да падне до ПИН екрана насред смяна.
 *
 * ВИНАГИ към облака, не през `authBaseUrl()`: това е единствената заявка,
 * която има смисъл само там — касата не знае за нея. (Викачът
 * `lib/edge/edgeMode.ts::leaveLocalMode` вече е върнал режима на `cloud`,
 * така че адресът и без това би бил същият; изричният избор е, за да не
 * зависи от реда на редовете там.)
 *
 * Отговорът е със същата форма като нормалния вход. Всеки отказ — мрежа,
 * 401 (невалиден или изтекъл билет), 409 (няма място по лиценз/капацитет),
 * 404 (облак, на който размяната е ИЗКЛЮЧЕНА, или по-стар) — се вдига към
 * викача, който пази ДНЕШНОТО поведение: обратната съвместимост със стар облак
 * не е по избор.
 *
 * ВАЖНО (22.09.2026): в облака размяната стои ИЗКЛЮЧЕНА по подразбиране
 * (`BE app/external/users/router_edge_ticket.py`, находка Б1 на червения
 * отбор — локалният билет пътува по LAN по обикновен HTTP и в `?ticket=` на
 * WebSocket-а, тоест размяната превръща подслушан кадър в облачен токен).
 * Този код остава, защото решението е на собственика и е една настройка — а
 * дотогава пътят е точно както преди размяната.
 *
 * 200 не е достатъчно: суровият axios НЕ минава през интерсептора, значи и
 * през `isCloudOwnResponse` — единствената проверка „наш ли е отговорът",
 * която целият останал код прави заради прихващащите портали. Хотелски портал,
 * върнал 200 с HTML, даваше `access_token: undefined`, размяната се смяташе за
 * успешна и служителят продължаваше смяната под акаунта на ТЕРМИНАЛА (находка
 * Н3). Затова формата се проверява тук.
 */
export async function exchangeEdgeTicket(
  ticket: string,
): Promise<LoginResponse> {
  const response = await axios.post<LoginResponse>(
    `${CLOUD_AUTH_BASE_URL}/user/exchange-edge-ticket`,
    { ticket },
    // Това Е вход, само че с билет вместо парола — същият ред от таблицата
    // (`api/timeouts.ts`). Суровият axios заобикаля интерсептора, значи и
    // бюджета по маршрут, а без него заявката виси до таймаута на ОС-а.
    { timeout: BUDGET_LOGIN_MS },
  );

  const session = response.data;
  if (!session || typeof session.access_token !== "string" || !session.access_token) {
    throw new Error("exchange-edge-ticket: отговорът не е на облака");
  }

  return session;
}

export async function getCurrentUserInfo(options?: {
  skipAuthRedirect?: boolean;
}): Promise<UserInfoResponse> {
  const response = await api.get<UserInfoResponse>("/user/info", {
    skipAuthRedirect: options?.skipAuthRedirect,
  });

  return response.data;
}

export async function getUsers(
  role?: string,
  reports?: boolean,
  without?: string,
): Promise<UserListItemOut[]> {
  const params: Record<string, string | boolean> = {};
  if (role) params.role = role;
  if (reports) params.reports = reports;
  if (without) params.without = without;

  const response = await api.get<UserListItemOut[]>("/user/users", {
    params: Object.keys(params).length > 0 ? params : undefined,
  });

  return response.data;
}

export interface OrganizationOut {
  name: string | null;
}

// Име на организацията (organizations.name от org DB) — за адреса ѝ
// https://{name}.unrealsoft.cloud (показва се при устройствата).
export async function getOrganization(): Promise<OrganizationOut> {
  const response = await api.get<OrganizationOut>("/user/organization");

  return response.data;
}

export interface VerifyPasswordRequest {
  password: string;
}

export interface VerifyPasswordResponse {
  valid: boolean;
}

export async function verifyPassword(
  password: string,
): Promise<VerifyPasswordResponse> {
  const response = await api.post<VerifyPasswordResponse>(
    "/user/verify-password",
    { password },
  );

  return response.data;
}

/**
 * Потвърждаването на ИЗХОДА от общ терминал — облачно или срещу касата.
 *
 * В облака това е `POST /user/verify-password` (проверява паролата на
 * притежателя на токена). Без интернет такъв endpoint НЯМА: касата сервира
 * само двата входа (§14.3.1), а в ограничен режим интерсепторът отказва
 * всичко останало. Затова досега изходът от общ терминал офлайн беше
 * невъзможен — човекът оставаше заключен на ПИН екрана и нямаше как да стигне
 * до екрана за потребител и парола.
 *
 * Офлайн проверката е `POST {edge}/user/verify-password` — огледало на облачния
 * endpoint: 200 с `{valid}` и нищо друго. Заявката е със суров axios по същата
 * причина като двата входа: интерсепторът на `api/client.ts` отказва всичко
 * друго към касата в ограничен режим.
 *
 * Досега (0.0.270 преди ревюто) се питаше самият ВХОД на касата. Две цени,
 * и двете реални (находка Н6): пет сбъркани потвърждения на изхода вдигаха
 * брояча на ВХОДА и заключваха терминала да влезе за 15 минути; а всяко
 * потвърждаване раждаше 24-часов локален билет, който FE-то хвърля. Каса от
 * преди 1.92.4 не знае новия път и отговаря 404 — тогава се пада обратно на
 * входа, за да не откажем нещо, което минава днес (инвариант 15).
 *
 * `username` е името на акаунта, чиято парола се проверява (притежателят на
 * токена, с който би тръгнала облачната заявка). Няма ли го — или няма адрес
 * на каса — се минава по ДНЕШНИЯ път: без име не се гадае чия парола се
 * проверява, а поправка, която отказва нещо минаващо днес, не се прави
 * мълчаливо.
 */
export async function verifyPasswordForLogout(
  password: string,
  username: string | null | undefined,
): Promise<VerifyPasswordResponse> {
  const base = isEdgeLocalMode() ? getEdgeBaseUrl() : null;
  if (!base || !username) return verifyPassword(password);

  const body = {
    restaurant_name: getRestaurantName(),
    username,
    password,
  };
  try {
    const answer = await axios.post<VerifyPasswordResponse>(
      `${base}/user/verify-password`,
      body,
      { timeout: BUDGET_LOGIN_MS },
    );
    return { valid: answer.data?.valid === true };
  } catch (error: unknown) {
    // Каса отпреди 1.92.4: маршрутът го няма. Пада се на стария път — по-добре
    // с чуждия брояч, отколкото изход, който не може да се потвърди.
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return verifyPasswordAgainstTheTillLogin(base, body);
    }
    throw error;
  }
}

/** Стария път (каса < 1.92.4): самият вход. 401 = грешна парола. */
async function verifyPasswordAgainstTheTillLogin(
  base: string,
  body: { restaurant_name: string; username: string; password: string },
): Promise<VerifyPasswordResponse> {
  try {
    await axios.post(`${base}/user/login`, body, { timeout: BUDGET_LOGIN_MS });
    return { valid: true };
  } catch (error: unknown) {
    // 401 е единственият отказ, който значи „грешна парола" (касата връща
    // текста на BE-то). 403 деактивиран, 423 заключен, 429 твърде много
    // опити, 503 каса без snapshot — това не са отговори на зададения въпрос
    // и се вдигат нагоре, за да не чуе човекът „Невалидна парола" за нещо
    // друго.
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      return { valid: false };
    }
    throw error;
  }
}

export interface LogoutResponse {
  message: string;
}

export async function logout(): Promise<LogoutResponse> {
  // `skipAuthRedirect`: 401 от logout никога не е причина за възстановяване на
  // сесия — BE-то дори не влиза в handler-а и нищо в базата не се пипа. Всички
  // извикващи го викат в try/catch и сами решават какво следва.
  const response = await api.post<LogoutResponse>("/user/logout", undefined, {
    skipAuthRedirect: true,
  });

  return response.data;
}
